import { FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import StarIcon from "../../assets/svgs/common/star.svg";
import type {
  ControllerRenderProps,
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from "react-hook-form";
import { Button } from "../ui/button";
import { Uploader } from "../custom/uploader";
import UploadIcon from "../../assets/svgs/common/upload.svg";
import DeleteIcon from "../../assets/svgs/common/delete.svg";
import MediaCarousel from "../carousel/carousel.components";
import { CarouselItem } from "../ui/carousel";
import type { Accept } from "react-dropzone";
import DocumentIcon from "../../assets/svgs/common/document.svg";
import { formatBytes } from "../../utils/utils";
import type { IAssetMinimal } from "../../types/models/asset.models.types";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { useEffect, useState } from "react";

type FormAssetUploadProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  trackingName?: string;
  label: string;
  uploadType: string;
  required?: boolean;
  placeholder: string;
  description: string;
  maxFiles: number;
  multiple?: boolean;
  maxSize?: number;
  buttonLabel?: string;
};

export const FormAssetUpload = <T extends FieldValues>({
  form,
  name,
  trackingName,
  label,
  uploadType,
  required,
  placeholder,
  description,
  maxFiles,
  multiple,
  maxSize,
  buttonLabel,
}: FormAssetUploadProps<T>) => {
  const arrayofAssets = form.getValues(trackingName as Path<T>);
  const [assets, setAssets] = useState<IAssetMinimal[]>(arrayofAssets || []);

  const availableAssets =
    trackingName &&
    Array.isArray(form.getValues(trackingName as Path<T>)) &&
    arrayofAssets.length > 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (trackingName && availableAssets) {
      setAssets(arrayofAssets);
    }
  }, [trackingName, availableAssets, arrayofAssets]);

  const assetsLength = assets?.length || 0;

  console.log(assets, uploadType);

  const handleClearUpload = (
    name: Path<T>,
    assetId?: string,
    index?: number
  ) => {
    // Handle deleting existing assets from server
    if (assetId && trackingName) {
      const updatedAssets = assets.filter((asset) => asset.id !== assetId);
      setAssets(updatedAssets);
      // Update the trackingName field in the form
      form.setValue(
        trackingName as Path<T>,
        updatedAssets as PathValue<T, Path<T>>,
        { shouldDirty: true }
      );
      return;
    }

    // Handle deleting newly uploaded files
    if (index !== undefined) {
      const currentFiles = form.getValues(name) as File[];
      const updatedFiles = currentFiles.filter((_, i) => i !== index);
      form.setValue(name, updatedFiles as PathValue<T, Path<T>>, {
        shouldDirty: true,
      });
      return;
    }
  };
  const handleUploadChange = (field: Path<T>, files: File[]) => {
    let currentFiles = form.getValues(field) as File[] | undefined;
    if (!Array.isArray(currentFiles)) {
      currentFiles = [];
    }

    if (maxFiles > 1) {
      form.setValue(
        field,
        [...currentFiles, ...files] as PathValue<T, Path<T>>,
        {
          shouldDirty: true,
        }
      ); // Manually mark field as dirty
    } else if (maxFiles === 1) {
      if (assets?.length > 0) {
        handleClearUpload(name, assets[0].id);
      }
      form.setValue(field, files as PathValue<T, Path<T>>, {
        shouldDirty: true,
      }); // Manually mark field as dirty
    }
  };

  function AssetTypeOperator(file: File) {
    console.log(file.type);
    if (
      file.type === "image/jpeg" ||
      file.type === "image/jpg" ||
      file.type === "image/png"
    ) {
      return (
        <img
          src={URL.createObjectURL(file)}
          alt=""
          className="rounded-xl w-96 h-[430px] object-cover"
        />
      );
    }
    if (file.type === "video/mp4") {
      return (
        // biome-ignore lint/a11y/useMediaCaption: <explanation>
        <video
          src={URL.createObjectURL(file)}
          controls
          className="rounded-xl w-[298px] h-[430px] aspect-video object-cover"
        />
      );
    }
    if (file.type === "application/zip" || file.type === "image/svg+xml") {
      return (
        <div className="flex flex-col justify-center items-center gap-4 w-full h-40">
          <span className="flex flex-col items-center gap-4 w-full font-semibold">
            <DocumentIcon />
            <p className="w-64 text-center text-nowrap truncate overflow-hidden">
              {file.name}
            </p>
            <span className="text-secondary text-xs">
              {formatBytes(file.size)}
            </span>
          </span>
        </div>
      );
    }
  }
  function AcceptType(): Accept {
    if (uploadType === "all") {
      return {
        "application/zip": [],
        "video/mp4": [],
        "video/mp3": [],
        "image/jpg": [],
        "image/jpeg": [],
        "image/png": [],
        "image/svg+xml": [],
      };
    }
    if (uploadType === "video") {
      return {
        "video/mp4": [],
      };
    }
    if (uploadType === "image") {
      return {
        "image/jpg": [],
        "image/jpeg": [],
        "image/png": [],
      };
    }

    return {};
  }

  function RenderAssets<T extends FieldValues>(
    field: ControllerRenderProps<T, Path<T>>
  ) {
    if (uploadType === "all") {
      return (field.value && field.value?.length > 0) ||
        (availableAssets && uploadType === "all") ? (
        <ScrollArea className="flex flex-col pr-4 w-full">
          {field.value.map((file: File, index: number) => {
            return (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                key={index}
                className="flex flex-col justify-between items-center pt-6 rounded-xl w-full"
              >
                <div className="flex justify-between items-center w-full">
                  <div
                    className={
                      " w-64 font-semibold text-nowrap text-sm truncate overflow-hidden"
                    }
                  >
                    {file.name}
                  </div>
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                  <span
                    onClick={() => handleClearUpload(name, undefined, index)}
                    className={" cursor-pointer"}
                  >
                    <DeleteIcon />
                  </span>
                </div>
              </div>
            );
          })}
          {assetsLength > 0 &&
            assets?.map((asset: IAssetMinimal) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                key={asset.id}
                className="flex flex-col justify-between items-center pt-6 rounded-xl w-full"
              >
                <div className="flex justify-between items-center w-full">
                  <div className="w-64 font-semibold text-nowrap text-sm truncate overflow-hidden">
                    {asset.id}
                  </div>
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                  <span
                    onClick={() => handleClearUpload(name, asset.id)}
                    className={"cursor-pointer"}
                  >
                    <DeleteIcon />
                  </span>
                </div>
              </div>
            ))}
          <ScrollBar />
        </ScrollArea>
      ) : null;
    }
    if (
      uploadType === "image" &&
      (assetsLength > 0 || (field.value && field.value?.length > 0))
    ) {
      return (
        <MediaCarousel
          className="w-96"
          count={field.value?.length + assetsLength}
        >
          {field.value?.map((file: File, index: number) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <CarouselItem key={index}>
              <div className="flex flex-col justify-between items-center gap-4 py-4 pt-6 rounded-xl w-full">
                <div className="relative flex justify-center items-center gap-4 w-full">
                  <div className="top-0 absolute bg-gradient-to-b from-background to-transparent rounded-t-xl w-full h-10" />
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                  <span
                    className={"top-2 right-4 z-10 absolute cursor-pointer"}
                    onClick={() => handleClearUpload(name, undefined, index)}
                  >
                    <DeleteIcon />
                  </span>
                  {AssetTypeOperator(file)}
                  <div
                    className={
                      "left-4 bottom-4 absolute w-64 font-semibold text-nowrap text-sm truncate overflow-hidden"
                    }
                  >
                    {file.name}
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
          {assets.map((asset: IAssetMinimal) => (
            <CarouselItem key={asset.id}>
              <div className="flex flex-col justify-between items-center gap-4 py-4 pt-6 rounded-xl w-full">
                <div className="relative flex justify-center items-center gap-4 w-full">
                  <div className="top-0 absolute bg-gradient-to-b from-background to-transparent rounded-t-xl w-full h-10" />
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                  <span
                    className={"top-2 right-4 z-10 absolute cursor-pointer"}
                    onClick={() => handleClearUpload(name, asset.id)}
                  >
                    <DeleteIcon />
                  </span>
                  <img
                    src={asset.url}
                    alt=""
                    className="rounded-xl w-96 h-[430px] object-cover"
                  />
                  <div
                    className={
                      "bottom-4 left-4 absolute w-64 font-semibold text-nowrap text-sm truncate overflow-hidden"
                    }
                  >
                    {asset.id}
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </MediaCarousel>
      );
    }

    return (availableAssets && uploadType === "video") ||
      (field.value && field.value?.length > 0) ? (
      <MediaCarousel
        className="w-96"
        count={
          uploadType === "video"
            ? field.value?.length + assetsLength
            : field.value?.length
        }
      >
        {field.value.map((file: File, index: number) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
          <CarouselItem key={index}>
            <div className="flex flex-col justify-between items-center gap-4 py-4 pt-6 rounded-xl w-full">
              <div className="relative flex justify-center items-center gap-4 w-full">
                <div className="top-0 absolute bg-gradient-to-b from-background to-transparent rounded-t-xl w-full h-10" />
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                <span
                  className={`${uploadType === "image" ? "top-2 right-4" : "top-4 right-14"}  z-10 absolute cursor-pointer`}
                  onClick={() => handleClearUpload(name, undefined, index)}
                >
                  <DeleteIcon />
                </span>
                {AssetTypeOperator(file)}
                <div
                  className={`${uploadType === "image" ? "left-4 bottom-4" : "bottom-5 left-16"}  absolute w-64 font-semibold text-nowrap text-sm truncate overflow-hidden`}
                >
                  {file.name}
                </div>
              </div>
            </div>
          </CarouselItem>
        ))}

        {uploadType === "video" &&
          availableAssets &&
          assets?.map((asset: IAssetMinimal) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
            <CarouselItem key={asset.id}>
              <div className="flex flex-col justify-between items-center gap-4 py-4 pt-6 rounded-xl w-full">
                <div className="relative flex justify-center items-center gap-4 w-full">
                  <div className="top-0 absolute bg-gradient-to-b from-background to-transparent rounded-t-xl w-full h-10" />
                  {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                  <span
                    className={"top-4 right-14 z-10 absolute cursor-pointer "}
                    onClick={() => handleClearUpload(name, asset.id)}
                  >
                    <DeleteIcon />
                  </span>
                  {/* biome-ignore lint/a11y/useMediaCaption: <explanation> */}
                  <video
                    src={asset.url}
                    controls
                    className="rounded-xl w-[298px] h-[430px] aspect-video object-cover"
                  />

                  <div
                    className={
                      "bottom-5 left-16 absolute w-64 font-semibold text-nowrap text-sm truncate overflow-hidden "
                    }
                  >
                    {asset.id}
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
      </MediaCarousel>
    ) : null;
  }

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="h-full">
          <FormLabel className="flex items-center gap-1 font-semibold">
            {label} {required && <StarIcon />}
          </FormLabel>
          <FormControl className="flex flex-col justify-center items-center border border-border rounded-xl">
            <div className="relative flex flex-col justify-between px-6 w-full h-[580px]">
              <div className="top-0 absolute bg-gradient-to-b from-background to-transparent rounded-t-xl w-full h-10" />

              {RenderAssets(field)}

              <div
                className={`flex flex-col bg-transparent justify-center items-center w-full ${uploadType === "all" && ((field.value && field.value?.length > 0) || assetsLength > 0) ? "" : "h-full"}`}
              >
                <Uploader
                  className="relative flex flex-col justify-end items-center gap-4 py-6 w-full h-full cursor-pointer"
                  value={field.value}
                  onValueChange={(files) => handleUploadChange(name, files)}
                  maxFiles={maxFiles}
                  maxSize={maxSize}
                  multiple={multiple}
                  accept={AcceptType()}
                >
                  {(assetsLength > 0 &&
                    (uploadType === "video" ||
                      uploadType === "all" ||
                      uploadType === "image")) ||
                  (field.value && field.value?.length > 0) ? (
                    <div className="w-full">
                      <Button
                        type="button"
                        className={
                          "bg-tertiarybuttonbg py-6 border border-border w-full font-semibold text-sm "
                        }
                      >
                        {buttonLabel ? buttonLabel : "Upload More"}
                      </Button>
                    </div>
                  ) : (
                    <span className="flex flex-col items-center gap-1 h-3/5 font-semibold">
                      <UploadIcon />
                      {placeholder}
                      <span className="text-secondary text-sm">
                        {description}
                      </span>
                    </span>
                  )}
                </Uploader>
              </div>
            </div>
          </FormControl>
        </FormItem>
      )}
    />
  );
};
